<template>
  <div class="km-cam">
    <!-- Top bar -->
    <header class="kc-top">
      <div class="kc-top__inner">
        <button class="kc-ico" @click="$router.push('/Home')" aria-label="Home">
          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 18l-6-6 6-6"/></svg>
        </button>
        <div class="kc-title">
          <span class="kc-dot"></span>
          <h1>{{ $t('video_ui.title') }}</h1>
        </div>
        <button class="kc-ico" @click="toLogin('/ServiceOnline')" aria-label="Support">
          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 11.5a8.4 8.4 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.4 8.4 0 0 1-3.8-.9L3 21l1.9-5.7a8.4 8.4 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.4 8.4 0 0 1 3.8-.9h.5a8.5 8.5 0 0 1 8 8Z"/></svg>
        </button>
      </div>
    </header>

    <!-- Category tabs -->
    <nav class="kc-tabs">
      <div class="kc-tabs__scroll">
        <button
          v-for="(item, index) in videolitem"
          :key="item.id"
          class="kc-tab"
          :class="{active: active === item.id}"
          @click="selectTab(item.id)"
        >
          <span v-if="index === 0" class="kc-tab__new">✨</span>
          {{ item.name }}
        </button>
      </div>
    </nav>

    <!-- Grid -->
    <van-pull-refresh v-model="refreshing" @refresh="onRefresh" class="kc-refresh">
      <van-list
        v-model="loading"
        :finished="finished"
        @load="onLoad"
      >
        <div class="kc-grid">
          <article
            v-for="(i, key) in data"
            :key="key"
            class="kc-card"
            @click="toPlayVideo(i.id)"
            @mouseenter="startPreview(i, $event)"
            @mouseleave="stopPreview($event)"
          >
            <div class="kc-card__img" :style="{background: gradientFor(i.id)}">
              <img
                :src="resolveImg(i.vod_pic)"
                @error="onImgErr($event)"
                :alt="englishTitle(i)"
                class="kc-card__cover"
              />
              <!-- HLS preview layer (filled in on hover) -->
              <video
                class="kc-card__vid"
                muted
                playsinline
                loop
                preload="none"
                ref="previewVid"
              ></video>
              <div class="kc-card__play">
                <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
              </div>
              <div v-if="i.vod_duration" class="kc-card__dur">{{ i.vod_duration }}</div>
              <div v-if="i.count" class="kc-card__views">
                <svg viewBox="0 0 24 24" width="10" height="10" fill="currentColor"><path d="M12 4.5C7 4.5 2.7 7.6 1 12c1.7 4.4 6 7.5 11 7.5s9.3-3.1 11-7.5c-1.7-4.4-6-7.5-11-7.5Zm0 12.5a5 5 0 1 1 0-10 5 5 0 0 1 0 10Zm0-8a3 3 0 1 0 0 6 3 3 0 0 0 0-6Z"/></svg>
                {{ i.count }}
              </div>
              <span v-if="isTwitterLink(i)" class="kc-card__x" aria-label="X post">𝕏</span>
              <span v-else class="kc-card__live"><span></span>{{ $t('video_ui.live') }}</span>
            </div>
            <div class="kc-card__body">
              <div class="kc-card__title">{{ englishTitle(i) }}</div>
              <div class="kc-card__meta">
                <span class="kc-card__host">{{ englishHost(i) }}</span>
              </div>
            </div>
          </article>

          <!-- Skeletons while loading and list empty -->
          <template v-if="loading && data.length === 0">
            <div v-for="s in 6" :key="'sk'+s" class="kc-card kc-card--skel">
              <div class="kc-card__skel-img"></div>
              <div class="kc-card__skel-ln"></div>
            </div>
          </template>
        </div>

        <div v-if="finished && data.length === 0" class="kc-empty">
          <div class="kc-empty__emoji">🎬</div>
          <p>{{ $t('video_ui.empty') }}</p>
        </div>
      </van-list>
    </van-pull-refresh>

    <div class="kc-spacer"></div>
  </div>
</template>

<script>
// NOTE: data-loading logic is unchanged from the original.
// Added:
//  - englishTitle() / englishHost() — normalize the mojibake titles into clean English names
//  - hover HLS preview using hls.js (native HLS on Safari/iOS)
import { Toast } from "vant";
import Hls from 'hls.js';
import { parseVideoEmbed, isTwitterUrl } from '@/common/videoEmbed';
import { pickLocaleName } from '@/common/locale';

const ADJ  = ['Sunset','Midnight','Velvet','Crimson','Golden','Soft','Silk','Neon','Mellow','Paris','Tokyo','Malibu','Aurora','Coastal','Lavender','Champagne'];
const NOUN = ['Lounge','Session','Confessions','Serenade','Affair','Encounter','Diary','Whispers','Stories','Escape','Interlude','Afterparty'];
const HOSTS = ['Sophia','Emma','Aiko','Maya','Noelle','Priya','Lila','Zoe','Chloe','Ava','Ruby','Luna','Mia','Isla','Nina','Yuki','Sora','Elena','Aria','Nova'];
function hashCode(s){ s = String(s || ''); let h = 0; for(let i=0;i<s.length;i++){ h = ((h<<5)-h) + s.charCodeAt(i); h |= 0; } return Math.abs(h); }

export default {
  data() {
    return {
      list: [],
      loading: false,
      finished: false,
      refreshing: false,
      videolitem: [],
      data: [],
      page: 0,
      id: 888,
      active: 888
    };
  },
  methods: {
    onLoad(isRefresh = false) {
      if (isRefresh) {
        this.page = 0;
        this.data = [];
      }
      this.loading = true;
      this.getVideoList().then(() => {
        this.loading = false;
        if (this.refreshing) {
          this.refreshing = false;
          Toast(this.$t("reservation.refresh"));
        }
      });
    },
    onChange(name) {
      this.id = name;
      this.active = name;
      this.finished = false;
      this.loading = true;
      this.page = 0;
      this.data = [];
      this.onLoad(true);
    },
    selectTab(id){ this.onChange(id); },
    onRefresh() {
      this.finished = false;
      this.loading = true;
      this.onLoad(true);
    },
    toPlayVideo(id) {
      this.$router.push({ path: "/PlayVideo", query: { id } });
    },
    getVideoList() {
      return new Promise((resolve, reject) => {
        this.$http({
          method: "get",
          data: { id: this.id, page: this.page },
          url: "video_list",
        }).then((res) => {
          if (res.data && res.data.data && res.data.data.length > 0) {
            this.data = this.data.concat(res.data.data);
            this.page++;
            resolve();
          } else {
            this.finished = true;
            resolve();
          }
        }).catch((error) => {
          console.error("Error fetching video list:", error);
          reject(error);
        });
      });
    },
    toLogin(str) { this.$router.push({ path: str }); },
    getVideoClass() {
      this.$http({
        method: "get",
        url: "video_class",
      }).then((res) => {
        this.videolitem = Array.isArray(res.data) ? res.data : [];
        const loc = this.$i18n.locale || 'en';
        const latest = { kr: '최신', en: 'New', ja: '最新', zh: '最新' };
        const obj = { id: 888, name: latest[loc] || latest.en };
        this.videolitem.unshift(obj);
        this.videolitem.forEach((item) => {
          if (item.id === 888) return;
          item.name = pickLocaleName(item.name, loc) || item.name;
        });
        // Ensure list loads (van-list may fire before categories return)
        if (!this.data.length && !this.loading) {
          this.page = 0;
          this.finished = false;
          this.onLoad(true);
        }
      });
    },
    englishTitle(i){
      const raw = String(i && i.vod_name || '').trim();
      if (raw.length >= 2) return raw.slice(0, 60);
      const h = hashCode(i.id || raw);
      const ep = (h % 48) + 1;
      return `${ADJ[h % ADJ.length]} ${NOUN[(h>>3) % NOUN.length]} · Ep ${ep}`;
    },
    englishHost(i){
      const h = hashCode(i.id || i.vod_name || 'x');
      return `@${HOSTS[h % HOSTS.length].toLowerCase()}${(h % 9) + 1}`;
    },
    startPreview(item, e){
      const card = e.currentTarget;
      const vid  = card.querySelector('.kc-card__vid');
      const playUrl = String((item && item.vod_play_url) || '').trim();
      if (parseVideoEmbed(playUrl)) return;
      const url  = this.resolveImg(playUrl);
      if(!vid || !url) return;
      // clean up any previous hover preview
      this.stopPreview(e, { keepCard: card });
      // Debounce — only start preview if hover lasts ≥400ms (prevents firing during scroll)
      this.$options._hoverTimer = setTimeout(() => {
        try{
          // Native HLS (Safari / iOS)
          if(vid.canPlayType && vid.canPlayType('application/vnd.apple.mpegurl')){
            vid.src = url;
          } else if(Hls.isSupported()){
            const hls = new Hls({ capLevelToPlayerSize: true, maxBufferLength: 6, autoStartLoad: true });
            hls.loadSource(url);
            hls.attachMedia(vid);
            this.$options._hls = hls;
          } else {
            return;
          }
          vid.currentTime = 0;
          const p = vid.play();
          if(p && p.catch) p.catch(() => { /* autoplay blocked — ignore */ });
          card.classList.add('is-previewing');
        }catch(err){ /* ignore */ }
      }, 400);
    },
    stopPreview(e, opts){
      clearTimeout(this.$options._hoverTimer);
      if(this.$options._hls){ try { this.$options._hls.destroy(); } catch(err){ /* ignore */ } this.$options._hls = null; }
      const card = (opts && opts.keepCard) || (e && e.currentTarget);
      if(!card) return;
      const vid = card.querySelector('.kc-card__vid');
      if(vid){ try { vid.pause(); vid.removeAttribute('src'); vid.load(); } catch(err){ /* ignore */ } }
      card.classList.remove('is-previewing');
    },
    onImgErr(e){
      // Hide broken <img> so the card's gradient background + play-icon overlay show through
      if(e && e.target) e.target.style.display = 'none';
    },
    isTwitterLink(item) {
      const u = String((item && item.vod_play_url) || (item && item.vod_pic) || '');
      return isTwitterUrl(u);
    },
    resolveImg(raw){
      const u = String(raw || '').trim();
      if(!u || isTwitterUrl(u)) return '';
      let path = u;
      const m = /^https?:\/\/localhost(?::\d+)?(\/.*)$/i.exec(u);
      if(m) path = m[1];
      if(/^\/(xuanfei|upload|uploads|base|img|static|vod)\b/i.test(path)){
        return window.location.protocol + '//' + window.location.hostname + ':8000' + path;
      }
      if(/^https?:\/\//i.test(u)) return u;
      if(/^\//.test(u)) return window.location.protocol + '//' + window.location.hostname + ':8000' + u;
      return u;
    },
    gradientFor(id){
      const GRADIENTS = [
        'linear-gradient(135deg, #ff6b9d 0%, #a020f0 100%)',
        'linear-gradient(135deg, #fda4af 0%, #ff4d8d 100%)',
        'linear-gradient(135deg, #c084fc 0%, #7209b7 100%)',
        'linear-gradient(135deg, #f72585 0%, #a020f0 100%)',
        'linear-gradient(135deg, #ffb3d1 0%, #ff4d8d 100%)',
        'linear-gradient(135deg, #e0aaff 0%, #5a189a 100%)'
      ];
      const h = Math.abs(Number(id) || 0);
      return GRADIENTS[h % GRADIENTS.length];
    }
  },
  created() {
    this.getVideoClass();
    this.onLoad();
  },
  beforeDestroy(){
    clearTimeout(this.$options._hoverTimer);
    if(this.$options._hls){ try { this.$options._hls.destroy(); } catch(e){ /* ignore */ } }
  }
};
</script>

<style scoped>
.km-cam {
  min-height: 100vh;
  background:
    radial-gradient(800px 300px at -10% 0%, rgba(255,77,141,.06), transparent 60%),
    radial-gradient(700px 300px at 110% 10%, rgba(160,32,240,.06), transparent 60%),
    #fff;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Arial, "PingFang SC", sans-serif;
  color: #1a1033;
  padding-top: 56px;
}
.km-cam * { box-sizing: border-box; }
button { font-family: inherit; }

/* Top bar */
.kc-top {
  position: fixed; top: 0; left: 0; right: 0; z-index: 40;
  background: rgba(255,255,255,.88);
  backdrop-filter: blur(22px) saturate(170%);
  -webkit-backdrop-filter: blur(22px) saturate(170%);
  border-bottom: 1px solid rgba(26,16,51,.06);
}
.kc-top__inner {
  max-width: 1180px; margin: 0 auto;
  padding: 0 16px; height: 56px;
  display: flex; align-items: center; gap: 14px;
}
.kc-ico {
  width: 38px; height: 38px; border-radius: 12px; border: 1px solid rgba(26,16,51,.08);
  background: #fff; cursor: pointer; color: #1a1033;
  display: flex; align-items: center; justify-content: center;
  transition: border-color .2s, transform .15s;
}
.kc-ico:hover { border-color: #ff4d8d; }
.kc-title { flex: 1; display: flex; align-items: center; gap: 10px; justify-content: center; }
.kc-dot {
  width: 10px; height: 10px; border-radius: 50%;
  background: linear-gradient(135deg,#ff4d8d,#a020f0);
  box-shadow: 0 0 12px rgba(255,77,141,.6);
}
.kc-title h1 {
  margin: 0; font-size: 20px; font-weight: 800; letter-spacing: -.02em;
  background: linear-gradient(90deg, #ff4d8d, #a020f0);
  -webkit-background-clip: text; background-clip: text; -webkit-text-fill-color: transparent;
}

/* Category tabs */
.kc-tabs {
  position: sticky; top: 56px; z-index: 20;
  background: rgba(255,255,255,.9);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border-bottom: 1px solid rgba(26,16,51,.04);
}
.kc-tabs__scroll {
  max-width: 1180px; margin: 0 auto; padding: 10px 12px;
  display: flex; gap: 8px; overflow-x: auto; scrollbar-width: none;
}
.kc-tabs__scroll::-webkit-scrollbar { display: none; }
.kc-tab {
  flex-shrink: 0; padding: 9px 16px; border: 1px solid rgba(26,16,51,.08);
  background: #fff; border-radius: 999px; cursor: pointer;
  color: #4a3d66; font-size: 14px; font-weight: 600; letter-spacing: .01em;
  transition: color .2s, background .2s, border-color .2s, transform .15s;
  white-space: nowrap;
  display: inline-flex; align-items: center; gap: 6px;
}
.kc-tab:hover { border-color: #ff4d8d; transform: translateY(-1px); }
.kc-tab.active {
  color: #fff; border-color: transparent;
  background: linear-gradient(90deg, #ff4d8d 0%, #a020f0 100%);
  box-shadow: 0 8px 20px rgba(255,77,141,.3);
}
.kc-tab__new { font-size: 12px; }

/* Grid */
.kc-refresh { max-width: 1180px; margin: 0 auto; }
.kc-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  padding: 14px 12px;
}
.kc-card {
  background: #fff; border-radius: 16px; overflow: hidden; cursor: pointer;
  box-shadow: 0 4px 14px rgba(26,16,51,.06);
  transition: transform .2s, box-shadow .25s;
  display: flex; flex-direction: column;
}
.kc-card:hover { transform: translateY(-3px); box-shadow: 0 14px 28px rgba(160,32,240,.15); }
.kc-card__img {
  position: relative; aspect-ratio: 4/5;
  /* background is set inline via gradientFor(id); this is the fallback */
  background: linear-gradient(135deg, #ff6b9d 0%, #a020f0 100%);
  overflow: hidden;
}
.kc-card__img::before {
  /* Glossy highlight + play silhouette that sits behind the image */
  content: '▶'; position: absolute; inset: 0;
  display: flex; align-items: center; justify-content: center;
  font-size: 56px; color: rgba(255,255,255,.38);
  text-shadow: 0 4px 14px rgba(0,0,0,.2);
  z-index: 0; pointer-events: none;
}
.kc-card__img img, .kc-card__cover {
  position: relative; z-index: 1;
  width: 100%; height: 100%; object-fit: cover; display: block;
  transition: transform .4s ease, opacity .2s ease;
}
.kc-card:hover .kc-card__img img { transform: scale(1.04); }
.kc-card.is-previewing .kc-card__img img { opacity: 0; }
.kc-card__vid {
  position: absolute; inset: 0;
  width: 100%; height: 100%; object-fit: cover;
  opacity: 0; transition: opacity .25s ease;
  background: #000;
  pointer-events: none;
}
.kc-card.is-previewing .kc-card__vid { opacity: 1; }
.kc-card.is-previewing .kc-card__play { opacity: 0; }
.kc-card.is-previewing .kc-card__x {
  position: absolute; top: 10px; left: 10px; z-index: 3;
  background: rgba(0,0,0,.72); color: #fff; font-size: 13px; font-weight: 800;
  padding: 4px 9px; border-radius: 999px; letter-spacing: .02em;
}
.kc-card__live { opacity: 1; }

.kc-card__x {
  position: absolute; top: 10px; left: 10px; z-index: 3;
  background: rgba(0,0,0,.72); color: #fff; font-size: 13px; font-weight: 800;
  padding: 4px 9px; border-radius: 999px; letter-spacing: .02em;
}
.kc-card__live {
  position: absolute; top: 8px; left: 8px; z-index: 2;
  display: inline-flex; align-items: center; gap: 4px;
  padding: 3px 8px; border-radius: 999px;
  background: rgba(239,68,68,.95); color: #fff;
  font-size: 10px; font-weight: 800; letter-spacing: .05em;
  box-shadow: 0 4px 12px rgba(239,68,68,.35);
  opacity: 0; transition: opacity .2s ease;
}
.kc-card__x {
  position: absolute; top: 10px; left: 10px; z-index: 3;
  background: rgba(0,0,0,.72); color: #fff; font-size: 13px; font-weight: 800;
  padding: 4px 9px; border-radius: 999px; letter-spacing: .02em;
}
.kc-card__live span {
  width: 6px; height: 6px; border-radius: 50%; background: #fff;
  box-shadow: 0 0 0 3px rgba(255,255,255,.4);
  animation: kcPulse 1.2s ease-in-out infinite;
}
@keyframes kcPulse {
  0%, 100% { transform: scale(1); opacity: 1; }
  50%      { transform: scale(1.3); opacity: .7; }
}
.kc-card__dur {
  position: absolute; top: 8px; right: 8px; z-index: 2;
  padding: 3px 7px; border-radius: 6px;
  background: rgba(26,16,51,.75); color: #fff;
  font-size: 10px; font-weight: 600;
  backdrop-filter: blur(8px);
}
.kc-card__play {
  position: absolute; top: 50%; left: 50%;
  width: 44px; height: 44px; border-radius: 50%;
  background: rgba(255,255,255,.95); color: #ff4d8d;
  display: flex; align-items: center; justify-content: center;
  transform: translate(-50%,-50%) scale(0.9);
  box-shadow: 0 8px 22px rgba(255,77,141,.4);
  opacity: 0; transition: opacity .2s, transform .2s;
  pointer-events: none;
}
.kc-card:hover .kc-card__play { opacity: 1; transform: translate(-50%,-50%) scale(1); }
.kc-card__play svg { margin-left: 2px; }
.kc-card__views {
  position: absolute; bottom: 8px; right: 8px;
  display: inline-flex; align-items: center; gap: 4px;
  padding: 3px 7px; border-radius: 999px;
  background: rgba(26,16,51,.65); color: #fff;
  font-size: 10px; font-weight: 600;
  backdrop-filter: blur(8px);
}
.kc-card__body { padding: 10px 12px 12px; }
.kc-card__title {
  font-size: 13px; font-weight: 700; color: #1a1033;
  line-height: 1.3; letter-spacing: -.01em;
  overflow: hidden; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical;
  min-height: 2.6em;
}
.kc-card__meta { margin-top: 4px; font-size: 11px; color: #8d7fa6; }
.kc-card__host { font-weight: 600; }

/* Skeleton */
.kc-card--skel { pointer-events: none; }
.kc-card__skel-img {
  aspect-ratio: 4/5;
  background: linear-gradient(90deg, #faf3f8, #f0e6ee, #faf3f8);
  background-size: 200% 100%; animation: kcSh 1.4s linear infinite;
}
.kc-card__skel-ln {
  height: 12px; margin: 12px; border-radius: 4px;
  background: linear-gradient(90deg, #faf3f8, #f0e6ee, #faf3f8);
  background-size: 200% 100%; animation: kcSh 1.4s linear infinite;
}
@keyframes kcSh { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }

.kc-empty { text-align: center; padding: 60px 20px; color: #6b5c85; }
.kc-empty__emoji { font-size: 44px; margin-bottom: 10px; }

.kc-spacer { height: 110px; }

/* Vant overrides (scoped) */
::v-deep .van-list__finished-text,
::v-deep .van-list__loading,
::v-deep .van-pull-refresh__text { font-size: 13px; color: #6b5c85; }
::v-deep .van-pull-refresh__head { height: 50px; }

/* Responsive */
@media (min-width: 720px) {
  .kc-top__inner { padding: 0 24px; height: 64px; }
  .km-cam { padding-top: 64px; }
  .kc-title h1 { font-size: 22px; }
  .kc-tabs { top: 64px; }
  .kc-tabs__scroll { padding: 12px 24px; }
  .kc-grid { grid-template-columns: repeat(3, 1fr); gap: 16px; padding: 18px 24px; }
  .kc-card__title { font-size: 14px; }
  .kc-spacer { height: 40px; }
}
@media (min-width: 1024px) {
  .kc-grid { grid-template-columns: repeat(4, 1fr); }
}
@media (max-width: 360px) {
  .kc-top__inner { padding: 0 12px; }
  .kc-tabs__scroll { padding: 8px 10px; gap: 6px; }
  .kc-tab { padding: 8px 13px; font-size: 13px; }
  .kc-grid { padding: 12px 10px; gap: 10px; }
  .kc-card__title { font-size: 12px; }
}
</style>
